import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackConversion, setUserId } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Track login conversion
    trackConversion.login('email');
    setUserId(userData.id);
    
    return userData;
  }, []);

  const signup = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Track signup conversion
    trackConversion.signup('email');
    setUserId(userData.id);
    
    return userData;
  }, []);

  const sendMagicLink = useCallback(async (email) => {
    await axios.post(`${API}/auth/magic-link`, { email });
  }, []);

  const verifyMagicLink = useCallback(async (linkToken) => {
    const response = await axios.post(`${API}/auth/verify-magic-link`, { token: linkToken });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Track magic link login
    trackConversion.login('magic_link');
    setUserId(userData.id);
    
    return userData;
  }, []);

  const completeOnboarding = useCallback(async (data) => {
    await axios.post(`${API}/onboarding`, data);
    setUser(prev => prev ? { ...prev, onboarding_completed: true } : null);
    
    // Track onboarding completion
    trackConversion.onboardingComplete();
  }, []);

  const updateUser = useCallback((updatedData) => {
    setUser(prev => prev ? { ...prev, ...updatedData } : null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const value = {
    user,
    loading,
    token,
    login,
    signup,
    sendMagicLink,
    verifyMagicLink,
    completeOnboarding,
    updateUser,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
